import type { ClientLoaderFunctionArgs } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import type { PostInsightsData, TContentGateElement, TPostElement } from '@shared/dream-components/src';
import structuredClone from '@ungap/structured-clone';
import type { NodeContent } from '~/interfaces/page';
import type { PricingDetails, Subscription } from '~/interfaces/subscriptions';
import { fetchLikedPosts } from '~/services/fetchers/fetchLikedPosts';
import { fetchPostInsights } from '~/services/fetchers/fetchPostInsights';
import type { StaticPageLoaderData } from '~/services/static_page';
import { fetchPricingDetails } from '~/utils/fetchPricingDetails';
import { findPostIdsPostNodes } from '~/utils/findPostIdsPostNodes';
import { findMultipleTipTapContentNodes, findTipTapContentNodes } from '~/utils/findTipTapContentNode';
import getCookieValue from '~/utils/getCookieValue';
import { shouldClipAfterContentGate } from '~/utils/shouldClipAfterContentGate';

const staticPageClientLoader = async ({ request, serverLoader }: ClientLoaderFunctionArgs) => {
  const serverData = await serverLoader<StaticPageLoaderData>();
  if (!serverData?.site || !serverData?.page) {
    // show the refresh page message if we didn't get server data
    throw new Error('RefreshPageError');
  }
  const content = structuredClone(serverData?.page?.viewable_page_version?.content);
  if (!content) {
    return serverData;
  }
  const url = new URL(request.url);
  const searchParams = new URLSearchParams(url.search);

  // TODO: use findMultipleTipTapContentNodes here instead of calling findTipTapContentNodes multiple times
  const postNodes = findTipTapContentNodes(content as NodeContent, 'post');

  let foundNodes = findMultipleTipTapContentNodes(content as NodeContent, ['pricing', 'contentGate']);
  // Look for pricing nodes in contentGate if present
  if (
    foundNodes.contentGate &&
    !foundNodes.pricing &&
    (foundNodes.contentGate as TContentGateElement).attrs.popupContent
  ) {
    foundNodes = {
      ...foundNodes,
      ...findMultipleTipTapContentNodes((foundNodes.contentGate as TContentGateElement).attrs.popupContent, [
        'pricing',
      ]),
    };
  }
  const hasFoundNodes = Object.values(foundNodes || {}).some((node) => node !== null);

  if (!hasFoundNodes && postNodes.length === 0) {
    return serverData;
  }

  const requests = [];

  if (foundNodes.pricing) {
    requests.push(fetchPricingDetails());
  } else {
    requests.push(Promise.resolve(null));
  }

  const token = getCookieValue('token', document.cookie);
  if (foundNodes.contentGate && token) {
    const requestBody: { jwt_token: string; subscriber_id?: string } = { jwt_token: token };
    requests.push(
      fetch('/current_subscription', {
        method: 'POST',
        headers: {
          'Sec-Fetch-Dest': 'empty',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      }),
    );
  } else {
    requests.push(Promise.resolve(null));
  }

  let pricingData: PricingDetails | undefined;
  let hasPricingDataFetchError = false;
  let subscription: Subscription | undefined;
  let likedPosts: string[] = [];
  let postsInsights: Record<string, PostInsightsData> | undefined;

  if (postNodes.length > 0) {
    const postIds = findPostIdsPostNodes(postNodes as TPostElement[]);
    if (postIds.length > 0) {
      requests.push(fetchPostInsights(postIds));
      if (token) {
        requests.push(fetchLikedPosts());
      } else {
        requests.push(Promise.resolve(null));
      }
    } else {
      requests.push(Promise.resolve(null));
      requests.push(Promise.resolve(null));
    }
  } else {
    requests.push(Promise.resolve(null));
    requests.push(Promise.resolve(null));
  }

  const [pricingRes, subscriptionRes, postsInsightsRes, likedPostsRes] = await Promise.all(requests);

  if (pricingRes && 'tiers' in pricingRes) {
    pricingData = pricingRes as PricingDetails;
  } else {
    hasPricingDataFetchError = true;
  }

  if (subscriptionRes && (subscriptionRes as Response).ok) {
    subscription = await (subscriptionRes as Response).json();
  }

  let shouldClipContent = false;
  try {
    if (foundNodes.contentGate) {
      shouldClipContent = shouldClipAfterContentGate({
        contentGateNode: foundNodes.contentGate,
        subscription,
        cookiesHeader: document.cookie,
        currentTierId: searchParams.get('current_tier_id'),
      });
    }
  } catch (error: unknown) {
    Sentry.captureException(error);
  }

  if (postNodes.length > 0 && likedPostsRes && (likedPostsRes as Response).ok) {
    try {
      const likedPostsBody = await (likedPostsRes as Response).json();
      if ('post_ids' in likedPostsBody) {
        likedPosts = likedPostsBody.post_ids;
      }
    } catch (error: unknown) {
      Sentry.captureException(error);
    }
  }

  if (postNodes.length > 0 && postsInsightsRes && (postsInsightsRes as Response).ok) {
    try {
      const postsInsightsBody = await (postsInsightsRes as Response).json();
      if ('posts' in postsInsightsBody) {
        postsInsights = postsInsightsBody.posts;
      }
    } catch (error: unknown) {
      Sentry.captureException(error);
    }
  }

  return {
    ...serverData,
    page: {
      ...serverData.page,
      viewable_page_version: {
        ...serverData.page?.viewable_page_version,
        content,
      },
    },
    pricingData,
    hasPricingDataFetchError,
    hasPricingDataFetched: true,
    hasPostInsightsDataFetched: true,
    hasLikedPostsDataFetched: true,
    isContentClipped: shouldClipContent,
    subscription,
    postsInsights,
    likedPosts,
  };
};

staticPageClientLoader.hydrate = true;

export { staticPageClientLoader };
